import { useQuery } from '@tanstack/react-query'

import request, { requestFile, type ComplexResponseType } from './request'
import {
  zodAlertCountsResponseValidator,
  zodAlertResponseValidator,
  type AlertCountsResponse,
  type AlertResponse
} from './types/Alert'
import { zodDelegateResponseValidator, type DelegateResponse } from './types/Delegate'
import { zodCommentResponseValidator, type CommentResponse } from './types/Comment'
import { useCurrentOrgId } from '@/stores/authStore'
import { downloadBlob } from '@/utils/download'

type AlertExportScope = 'alerts' | 'all'

type AlertExportJobStatus = 'pending' | 'processing' | 'ready' | 'failed'

interface AlertExportJobResponse {
  status: AlertExportJobStatus
  downloadUrl?: string
  errorMessage?: string
}

export async function createAlertExportJob({
  scope,
  subjectId,
  days,
  assigned,
  assignedAlerts
}: {
  scope: AlertExportScope
  subjectId?: string
  days?: number
  assigned?: boolean
  assignedAlerts?: boolean
}): Promise<string> {
  const res = await request<{ jobId: string }>({
    endpoint: '/alert/export/jobs',
    method: 'POST',
    body: {
      scope,
      subjectId,
      days,
      assigned,
      assignedAlerts
    }
  })

  if (!res.success) throw new Error(res.message || 'Failed to start export')
  return res.data.jobId
}

export async function getAlertExportJob(jobId: string): Promise<AlertExportJobResponse> {
  const res = await request<AlertExportJobResponse>({
    endpoint: `/alert/export/jobs/${jobId}`,
    method: 'GET'
  })
  if (!res.success) throw new Error(res.message || 'Failed to check export status')
  return res.data
}

const getAssignedAlerts = async ({
  page, limit, filters
}: {
  page: number,
  limit: number,
  filters?: Record<string, string>
}) => {
  const queryParams: string[] = []
  if (page > 1) queryParams.push('page=' + page)
  if (limit > 1) queryParams.push('limit=' + limit)
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value) queryParams.push(`${key}=${value}`)
  })
  const res = await request<ComplexResponseType<AlertResponse[]>>({
    endpoint: `/alert/assigned?${queryParams.join('&')}`,
    method: 'GET'
  })
  if (res.data?.items?.length) zodAlertResponseValidator(res.data.items)
  return res
}

export const useGetAssignedAlerts = ({
  page, limit, filters
}: {
  page: number,
  limit: number,
  filters?: Record<string, string>
}) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'assignedAlerts', page, limit, filters],
    queryFn: () => getAssignedAlerts({ page, limit, filters }),
  })
}

const getAlertBySubjectId = async ({
  page, limit, subjectId, filters
}: {
  page: number,
  limit: number,
  subjectId: string,
  filters?: Record<string, string>
}) => {
  const queryParams: string[] = []
  if (page > 1) queryParams.push('page=' + page)
  if (limit > 1) queryParams.push('limit=' + limit)
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value) queryParams.push(`${key}=${value}`)
  })
  const res = await request<ComplexResponseType<AlertResponse[]>>({
    endpoint: `/alert?subjectId=${subjectId}&${queryParams.join('&')}`,
    method: 'GET'
  })
  if (res.data?.items?.length) zodAlertResponseValidator(res.data.items)
  return res
}

export const useGetAlertBySubjectId = ({
  subjectId, page, limit, filters
}: {
  subjectId: string | undefined,
  page: number,
  limit: number,
  filters?: Record<string, string>
}, enabled = true) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'alertBySubjectId', subjectId, page, limit, filters],
    queryFn: () => getAlertBySubjectId({ subjectId: subjectId || '', page, limit, filters }),
    enabled: !!subjectId && enabled
  })
}

const getAlert = async (alertId: string) => {
  const res = await request<{ alert: AlertResponse }>({
    endpoint: `/alert/${alertId}`,
    method: 'GET'
  })
  if (res.success) zodAlertResponseValidator([res.data.alert])
  return res
}

export const useGetAlert = (alertId: string | undefined) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'alert', alertId],
    queryFn: () => getAlert(alertId || ''),
    enabled: !!alertId
  })
}

const getAlertDelegates = async (alertId: string) => {
  const res = await request<DelegateResponse[]>({
    endpoint: `/alert/${alertId}/delegates`,
    method: 'GET'
  })
  if (res.data?.length) zodDelegateResponseValidator(res.data)
  return res
}

export const useGetAlertDelegates = (alertId: string | undefined) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'alert', alertId, 'delegates'],
    queryFn: () => getAlertDelegates(alertId || ''),
    enabled: !!alertId
  })
}

export const createAlert = async (subjectId: string, body: any) => {
  return request<any>({
    endpoint: `/subject/${subjectId}/reports`,
    method: 'POST',
    body,
    multipart: true,
  })
}

export const updateAlert = async (alertId: string, body: any) => {
  return request<any>({
    endpoint: `/alert/${alertId}`,
    method: 'POST',
    body
  })
}

const getAlertComments = async (alertId: string) => {
  const res = await request<CommentResponse[]>({
    endpoint: `/alert/${alertId}/comments`,
    method: 'GET'
  })
  if (res.data?.length) zodCommentResponseValidator(res.data)
  return res
}

export const useGetAlertComments = (alertId: string | undefined) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'alert', alertId, 'comments'],
    queryFn: () => getAlertComments(alertId || ''),
    enabled: !!alertId
  })
}

export const postAlertComment = async (alertId: string, message: string) => {
  return request<any>({
    endpoint: `/alert/${alertId}/comments`,
    method: 'POST',
    body: { content: message }
  })
}

const getAlertSources = async (subjectId: string) => {
  const res = await request<string[]>({
    endpoint: `/alert-sources?subjectId=${subjectId}`,
    method: 'GET'
  })
  return res
}

export const useGetAlertSources = (subjectId: string | undefined) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'alertSources', subjectId],
    queryFn: () => getAlertSources(subjectId || ''),
  })
}

const getAlertCountsBySubjectId = async (
  subjectId?: string,
  filters?: Record<string, string>,
  assigned?: boolean
) => {
  const queryParams: string[] = []
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value) queryParams.push(`${key}=${value}`)
  })
  if (assigned !== undefined) queryParams.push(`assigned=${assigned}`)
  if (subjectId !== undefined) queryParams.push(`subjectId=${subjectId}`)
  const res = await request<AlertCountsResponse>({
    endpoint: `/alert/counts?${queryParams.join('&')}`,
    method: 'GET'
  })
  if (res.data) zodAlertCountsResponseValidator(res.data)
  return res
}

export const useGetAlertCountsBySubjectId = (
  subjectId: string | undefined,
  filters?: Record<string, string>,
) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'alertCounts', subjectId, filters, false],
    queryFn: () => getAlertCountsBySubjectId(subjectId, filters, false),
  })
}

export const useGetAssignedAlertCounts = (
  filters?: Record<string, string>,
) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'alertCounts', 'assigned', filters, true],
    queryFn: () => getAlertCountsBySubjectId(undefined, filters, true),
  })
}

export const exportAlerts = async ({
  scope,
  subjectId,
  days,
  assigned,
  assignedAlerts
}: {
  scope: AlertExportScope
  subjectId?: string
  days?: number
  /**
   * Broader “subjects I’m assigned to” org slice (not “this subject only”).
   * Prefer omitting for per-individual export; use subjectId without assigned/assignedAlerts.
   */
  assigned?: boolean
  /** My Alerts: rows tied to alert IDs assigned/escalated to the current user */
  assignedAlerts?: boolean
}): Promise<void> => {
  const params = new URLSearchParams()
  params.set('scope', scope)
  if (subjectId) params.set('subjectId', subjectId)
  if (days !== undefined) params.set('days', String(days))
  if (assigned !== undefined) params.set('assigned', String(assigned))
  if (assignedAlerts !== undefined) params.set('assignedAlerts', String(assignedAlerts))
  const { blob, filename } = await requestFile({
    endpoint: `/alert/export?${params.toString()}`,
    method: 'GET',
    timeoutMs: 180_000
  })
  downloadBlob(
    blob,
    filename || `alerts-export-${new Date().toISOString().slice(0, 10)}.xlsx`
  )
}
